import { useFormik } from "formik";
import { useEffect } from "react";
import { Button, Modal } from "react-bootstrap";
import { createPortal } from "react-dom";
import * as Yup from "yup";
import { KTIcon } from "../../../_metronic/helpers";
import { AnnouncementData } from "../hooks/useAdminAnnouncements";

interface AddEditModalProps {
  show: boolean;
  handleClose: () => void;
  editData: AnnouncementData | null;
  isUpdateMode: boolean;
  onSave: (values: { title: string; description: string }) => Promise<boolean>;
}

const modalsRoot = document.getElementById("root-modals") || document.body;

const AddEditAnnouncementModal: React.FC<AddEditModalProps> = ({
  show,
  handleClose,
  editData,
  isUpdateMode,
  onSave,
}) => {
  const announcementSchema = Yup.object().shape({
    title: Yup.string()
      .max(200, "Title cannot exceed 200 characters")
      .required("Title is required"),
    description: Yup.string()
      .max(2000, "Description cannot exceed 2000 characters")
      .required("Description is required"),
  });

  const formik = useFormik({
    initialValues: {
      id: editData?.id || 0,
      title: editData?.title ?? "",
      description: editData?.description ?? "",
    },
    validationSchema: announcementSchema,
    onSubmit: async (values) => {
      const isSaved = await onSave(values);
      if (isSaved) {
        formik.resetForm();
      }
    },
  });

  useEffect(() => {
    if (editData) {
      formik.setValues({
        id: editData.id,
        title: editData.title,
        description: editData.description,
      });
    } else {
      formik.resetForm();
    }
  }, [editData, show]);

  return createPortal(
    <Modal
      id="kt_modal_announcement"
      tabIndex={-1}
      aria-hidden="true"
      dialogClassName="modal-dialog modal-dialog-centered mw-600px"
      show={show}
      onHide={() => {
        if (!formik.isSubmitting) {
          handleClose();
        }
      }}
      backdrop={true}
      keyboard={!formik.isSubmitting}
    >
      <div className="modal-header">
        <h2>
          {isUpdateMode
            ? "Update Announcement"
            : editData
            ? "Re-Notify Announcement"
            : "Create Announcement"}
        </h2>
        <div
          className="btn btn-sm btn-icon btn-active-color-primary"
          onClick={() => {
            if (!formik.isSubmitting) {
              handleClose();
            }
          }}
          style={{
            pointerEvents: formik.isSubmitting ? "none" : "auto",
            opacity: formik.isSubmitting ? 0.5 : 1,
          }}
        >
          <KTIcon className="fs-1" iconName="cross" />
        </div>
      </div>

      <div className="p-6">
        <form onSubmit={formik.handleSubmit} noValidate className="form">
          <div>
            <div className="fv-row mb-8">
              <div className="d-flex justify-content-between align-items-center">
                <label className="form-label fw-bolder text-dark fs-6 mb-0">
                  Title
                </label>
                <span className="text-muted fs-7">
                  {formik.values.title.length}/200
                </span>
              </div>
              <input
                placeholder="Enter title"
                type="text"
                autoComplete="off"
                {...formik.getFieldProps("title")}
                maxLength={200}
                className="form-control form-control-lg form-control-solid mb-3 mb-lg-0"
                disabled={formik.isSubmitting}
              />
              {formik.touched.title && formik.errors.title && (
                <div className="fv-plugins-message-container">
                  <div className="fv-help-block">
                    <p style={{ color: "red" }}>{formik.errors.title}</p>
                  </div>
                </div>
              )}
            </div>
            <div className="fv-row mb-8">
              <div className="d-flex justify-content-between align-items-center">
                <label className="form-label fw-bolder text-dark fs-6 mb-0">
                  Description
                </label>
                <span className="text-muted fs-7">
                  {formik.values.description.length}/2000
                </span>
              </div>
              <textarea
                placeholder="Enter description"
                {...formik.getFieldProps("description")}
                maxLength={2000}
                className="form-control form-control-lg form-control-solid mb-3 mb-lg-0"
                rows={4}
                style={{ resize: "none" }}
                disabled={formik.isSubmitting}
              />
              {formik.touched.description && formik.errors.description && (
                <div className="fv-plugins-message-container">
                  <div className="fv-help-block">
                    <p style={{ color: "red" }}>{formik.errors.description}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="w-100 d-flex p-5 align-items-center">
            <Button
              onClick={() => {
                if (!formik.isSubmitting) {
                  formik.resetForm();
                  handleClose();
                }
              }}
              size="lg"
              variant="secondary"
              disabled={formik.isSubmitting}
            >
              Cancel
            </Button>
            <div className="w-15px"></div>
            <Button
              type="submit"
              size="lg"
              variant="primary"
              disabled={formik.isSubmitting}
            >
              {formik.isSubmitting
                ? "Loading..."
                : isUpdateMode
                ? "Update"
                : "Notify All"}
            </Button>
          </div>
        </form>
      </div>
    </Modal>,
    modalsRoot,
  );
};

export default AddEditAnnouncementModal;
